import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  statusEnquiryService,
  StatusEnquiry,
  StatusEnquiryFormData,
  StatusEnquiryListParams
} from '@/services/statusEnquiry/statusEnquiryService';

export interface StatusEnquiryState {
  statusEnquiries: StatusEnquiry[];
  statusEnquiry: StatusEnquiry | null;
  statusEnquiriesLoading: boolean;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: StatusEnquiryState = {
  statusEnquiries: [],
  statusEnquiry: null,
  statusEnquiriesLoading: false,
  isLoading: false,
  error: null,
  pagination: null,
};

export const fetchStatusEnquiries = createAsyncThunk(
  'statusEnquiry/fetchStatusEnquiries',
  async (params: StatusEnquiryListParams, { rejectWithValue }) => {
    try {
      const response = await statusEnquiryService.getStatusEnquiries(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch status enquiries');
    }
  }
);

export const createStatusEnquiry = createAsyncThunk(
  'statusEnquiry/createStatusEnquiry',
  async (data: StatusEnquiryFormData, { rejectWithValue }) => {
    try {
      const response = await statusEnquiryService.createStatusEnquiry(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create status enquiry');
    }
  }
);

export const updateStatusEnquiry = createAsyncThunk(
  'statusEnquiry/updateStatusEnquiry',
  async ({ id, data }: { id: string; data: StatusEnquiryFormData }, { rejectWithValue }) => {
    try {
      const response = await statusEnquiryService.updateStatusEnquiry(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update status enquiry');
    }
  }
);

export const deleteStatusEnquiry = createAsyncThunk(
  'statusEnquiry/deleteStatusEnquiry',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await statusEnquiryService.deleteStatusEnquiry(id);
      return { ...response, id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete status enquiry');
    }
  }
);

export const fetchStatusEnquiryById = createAsyncThunk(
  'statusEnquiry/fetchStatusEnquiryById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await statusEnquiryService.getStatusEnquiryById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch status enquiry');
    }
  }
);

export const statusEnquirySlice = createSlice({
  name: 'statusEnquiry',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStatusEnquiry: (state) => {
      state.statusEnquiry = null;
    },
    setStatusEnquiries: (state, action: PayloadAction<StatusEnquiry[]>) => {
      state.statusEnquiries = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Status Enquiries
      .addCase(fetchStatusEnquiries.pending, (state) => {
        state.statusEnquiriesLoading = true;
        state.error = null;
      })
      .addCase(fetchStatusEnquiries.fulfilled, (state, action) => {
        state.statusEnquiriesLoading = false;
        state.statusEnquiries = action.payload.data.docs;
        state.pagination = {
          currentPage: action.payload.data.page,
          totalPages: action.payload.data.totalPages,
          totalItems: action.payload.data.totalDocs,
          itemsPerPage: action.payload.data.limit,
        };
        state.error = null;
      })
      .addCase(fetchStatusEnquiries.rejected, (state, action) => {
        state.statusEnquiriesLoading = false;
        state.error = action.payload as string;
      })

      // Create Status Enquiry
      .addCase(createStatusEnquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createStatusEnquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusEnquiries.unshift(action.payload.data);
        state.error = null;
      })
      .addCase(createStatusEnquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Status Enquiry
      .addCase(updateStatusEnquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStatusEnquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedStatusEnquiry = action.payload.data;
        const index = state.statusEnquiries.findIndex(item => item._id === updatedStatusEnquiry._id);
        if (index !== -1) {
          state.statusEnquiries[index] = updatedStatusEnquiry;
        }
        state.error = null;
      })
      .addCase(updateStatusEnquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete Status Enquiry
      .addCase(deleteStatusEnquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteStatusEnquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusEnquiries = state.statusEnquiries.filter(item => item._id !== action.payload.id);
        state.error = null;
      })
      .addCase(deleteStatusEnquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Status Enquiry By ID
      .addCase(fetchStatusEnquiryById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStatusEnquiryById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusEnquiry = action.payload.data;
        state.error = null;
      })
      .addCase(fetchStatusEnquiryById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearStatusEnquiry, setStatusEnquiries } = statusEnquirySlice.actions;
export default statusEnquirySlice.reducer;
